import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    TextField,
    Dropdown,
    IDropdownOption,
    MessageBarType,
    Stack,
    Label,
    IColumn,
    MessageBar, IStackStyles
} from '@fluentui/react';
import { useAuth } from "react-oidc-context";
import { displayMessageBar } from '../../LayoutWithAuth/LayoutWithAuth';
import strings from '../../../loc/strings';
import {
    createCAEndUserAssignment,
    updateCAEndUserAssignment,
    CAEndUserAssignmentSummary,
    EUSectoralLegislatureBasis,
    CAEndUserAssignmentCreate,
    CAEndUserAssignmentUpdate
} from "../../../services/AdminServices/CAEndUserAssignmentService";

interface CAEndUserAssignmentModalProps {
    handleActionResponse: (response: string) => void;
    options: IColumn[];
    isEdit: boolean;
    isAddNew: boolean;
    resetIsEdit: (response: string) => void;
    editItem: CAEndUserAssignmentSummary | null;
    availableUsers: string[];
    activeLegalBases: EUSectoralLegislatureBasis[];
}

function CAEndUserAssignmentModal({
    handleActionResponse,
    options,
    isEdit,
    isAddNew,
    resetIsEdit,
    editItem,
    availableUsers,
    activeLegalBases
}: CAEndUserAssignmentModalProps) {
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        userEmail: '',
        selectedLegalBases: [] as number[]
    });
    const [errors, setErrors] = useState({
        userEmail: '',
        selectedLegalBases: ''
    });

    const isOpen = isEdit || isAddNew;
    const modalTitle = isEdit ? 'Edit assignment' : 'Add assignment';

    const dialogContentProps = {
        type: DialogType.normal,
        title: modalTitle,
    };

    const modalProps = React.useMemo(
        () => ({
            isBlocking: true,
        }),
        [],
    );

    // Initialize form data when modal opens
    useEffect(() => {
        if (isEdit && editItem) {
            setFormData({
                userEmail: editItem.userEmail,
                selectedLegalBases: editItem.assignedLegalBases.map(lb => lb.id)
            });
        } else if (isAddNew) {
            setFormData({
                userEmail: '',
                selectedLegalBases: []
            });
        }
        setErrors({ userEmail: '', selectedLegalBases: '' });
    }, [isEdit, isAddNew, editItem]);

    // Convert available users to dropdown options
    const userOptions: IDropdownOption[] = availableUsers.map(user => ({
        key: user,
        text: user
    }));

    // Convert legal bases to dropdown options
    const legalBasisOptions: IDropdownOption[] = activeLegalBases.map(legalBasis => ({
        key: legalBasis.id,
        text: legalBasis.name,
        selected: formData.selectedLegalBases.includes(legalBasis.id)
    }));

    // Handle form validation
    const validateForm = (): boolean => {
        const newErrors = { userEmail: '', selectedLegalBases: '' };
        let isValid = true;

        if (!formData.userEmail.trim()) {
            newErrors.userEmail = 'User email is required';
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.userEmail)) {
            newErrors.userEmail = 'Please enter a valid email address';
            isValid = false;
        }

        if (formData.selectedLegalBases.length === 0) {
            newErrors.selectedLegalBases = 'At least one legal basis must be selected';
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            if (isEdit && editItem) {
                const updateData: CAEndUserAssignmentUpdate = {
                    id: 0, // This will be set by the backend
                    userEmail: formData.userEmail,
                    euSectoralLegislatureBasisIds: formData.selectedLegalBases,
                    ncaCode: '' // This will be set by the backend
                };

                await updateCAEndUserAssignment(editItem.userEmail, updateData, auth.user?.id_token);
                handleActionResponse('edittrue');
            } else {
                const createData: CAEndUserAssignmentCreate = {
                    userEmail: formData.userEmail,
                    euSectoralLegislatureBasisIds: formData.selectedLegalBases,
                    ncaCode: '' // This will be set by the backend
                };

                await createCAEndUserAssignment(createData, auth.user?.id_token);
                handleActionResponse('createtrue');
            }
            
            resetIsEdit('close');
        } catch (error: any) {
            console.error('Error saving assignment:', error);
            const errorMessage = error.message || 'An error occurred while saving the assignment';
            handleActionResponse(`error: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        resetIsEdit('close');
    };

    // Handle user email change
    const handleUserEmailChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setFormData(prev => ({ ...prev, userEmail: newValue || '' }));
        if (errors.userEmail) {
            setErrors(prev => ({ ...prev, userEmail: '' }));
        }
    };

    // Handle user email dropdown change
    const handleUserEmailDropdownChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            setFormData(prev => ({ ...prev, userEmail: option.key as string }));
            if (errors.userEmail) {
                setErrors(prev => ({ ...prev, userEmail: '' }));
            }
        }
    };

    // Handle legal basis selection change
    const handleLegalBasisChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            const legalBasisId = option.key as number;
            setFormData(prev => ({
                ...prev,
                selectedLegalBases: option.selected 
                    ? [...prev.selectedLegalBases, legalBasisId]
                    : prev.selectedLegalBases.filter(id => id !== legalBasisId)
            }));
            
            if (errors.selectedLegalBases) {
                setErrors(prev => ({ ...prev, selectedLegalBases: '' }));
            }
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={handleCancel}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            maxWidth="560px"
        >
            <Stack tokens={{ childrenGap: 16 }}>
                {isAddNew && availableUsers.length === 0 && (
                    <MessageBar messageBarType={MessageBarType.info}>
                        {strings.noUsersAvailableForAssignment}
                    </MessageBar>
                )}
                <div>
                    <Label required={true}>User email</Label>
                    {isAddNew ? (
                        <Dropdown
                            placeholder="Select a user email"
                            options={userOptions}
                            selectedKey={formData.userEmail}
                            onChange={handleUserEmailDropdownChange}
                            errorMessage={errors.userEmail}
                            disabled={availableUsers.length === 0}
                        />
                    ) : (
                        <TextField
                            value={formData.userEmail}
                            onChange={handleUserEmailChange}
                            errorMessage={errors.userEmail}
                            disabled={isEdit} // Don't allow changing email when editing
                        />
                    )}
                </div>

                <div>
                    <Label required={true}>EU sectoral legislative bases</Label>
                    <Dropdown
                        placeholder="Select EU sectoral legislative bases"
                        options={legalBasisOptions}
                        onChange={handleLegalBasisChange}
                        multiSelect
                        errorMessage={errors.selectedLegalBases}
                        disabled={isAddNew && availableUsers.length === 0}
                    />
                </div>
            </Stack>

            <DialogFooter>
                <PrimaryButton
                    text={isEdit ? "Update" : "Add"}
                    onClick={handleSubmit}
                    disabled={isLoading || (isAddNew && availableUsers.length === 0)}
                />
                <DefaultButton
                    text="Cancel"
                    onClick={handleCancel}
                    disabled={isLoading}
                />
            </DialogFooter>
        </Dialog>
    );
}

export default CAEndUserAssignmentModal;
